import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  ShieldCheck,
  Clock3,
  MapPin,
  Pizza as PizzaIcon,
  Beef,
  Sandwich,
  Flame,
  UtensilsCrossed,
  Leaf,
  Coffee,
  IceCream,
  Tag,
  Truck,
  ExternalLink,
} from "lucide-react";
import { HOME } from "@/lib/testIds";
import {
  ASSETS,
  RESTAURANT,
  HALAL,
  OFFERS,
  COPY,
  PLATFORMS,
} from "@/data/catalog";
import { DeliveryDialog } from "@/components/DeliveryDialog";
import { HowToOrderBlock } from "@/components/HowToOrderBlock";
import { Logo } from "@/components/brand/Logo";
import { useRestaurant } from "@/lib/brandingStore";
import { Seo } from "@/lib/seo";

/* -------------------------------------------------------------------------- */
/*  Petits composants utilitaires                                              */
/* -------------------------------------------------------------------------- */

const NonContractual = ({ className = "" }) => (
  <div
    className={`absolute bottom-1 right-2 text-[10px] text-white/45 italic ${className}`}
  >
    {COPY.nonContractual}
  </div>
);

/* -------------------------------------------------------------------------- */
/*  HERO                                                                       */
/* -------------------------------------------------------------------------- */

const Hero = () => (
  <section
    data-testid={HOME.hero}
    className="relative overflow-hidden border-b border-[#262626]"
  >
    {/* Halo bleu nuit doux + voile orange ultra discret */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-[460px] max-w-3xl"
      style={{
        background:
          "radial-gradient(55% 75% at 50% 14%, rgba(30,58,95,0.35) 0%, rgba(15,30,55,0.18) 38%, transparent 72%)",
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-[420px] max-w-3xl"
      style={{
        background:
          "radial-gradient(50% 70% at 50% 18%, rgba(255,149,0,0.05) 0%, rgba(255,149,0,0.015) 40%, transparent 70%)",
      }}
    />

    <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-10 sm:pt-14 lg:pt-20 pb-12">
      {/* Bloc identité — wordmark HTML/CSS, plus aucune image PNG */}
      <div className="flex flex-col items-center text-center">
        <Logo size="hero" tone="light" tagline align="center" />
        <div className="mt-6 sm:mt-7 text-[11px] uppercase tracking-[0.42em] text-[#F5EFE6]/55 font-street">
          Fait maison · Halal sélectionné
        </div>
        <p className="mt-5 text-sm sm:text-base text-[#A1A1AA] leading-relaxed max-w-xl">
          Pizzas signatures, Naan Burger, Naan Rolls, Starters et desserts.
          Fait maison à Villiers-le-Bel.
        </p>

        {/* CTAs principaux */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Link
            to="/menu"
            data-testid="hero-configurator-cta"
            className="font-street inline-flex items-center justify-center gap-2 rounded-md bg-[#FF9500] px-6 py-3.5 text-base tracking-wider text-black shadow-[0_5px_0_#7a4900] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#FFAA1F] hover:shadow-[0_7px_0_#7a4900]"
          >
            <ArrowRight size={18} strokeWidth={2.6} />
            Voir la carte
          </Link>
          <a
            href={RESTAURANT.phoneTel}
            data-testid="hero-call-cta"
            className="font-street inline-flex items-center justify-center gap-2 rounded-md border border-[#F5EFE6]/25 bg-transparent px-6 py-3.5 text-base tracking-wider text-[#F5EFE6] transition-all hover:border-[#F5EFE6]/45 hover:bg-white/5"
          >
            <Phone size={16} className="text-[#C9A875]" />
            Appeler
          </a>
        </div>

        {/* Pills livraison + halal — ton ivoire discret, accent orange réservé au seul CTA */}
        <div className="mt-5 flex flex-wrap gap-2 items-center justify-center">
          <DeliveryDialog
            trigger={
              <button
                type="button"
                data-testid="hero-delivery-trigger"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#F5EFE6]/15 bg-[#F5EFE6]/[0.03] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#F5EFE6]/75 font-street hover:border-[#F5EFE6]/30 hover:text-[#F5EFE6] transition-colors"
              >
                <Truck size={12} className="opacity-70" />
                Livraison · bientôt
              </button>
            }
          />
          <a
            href="#halal"
            className="inline-flex w-fit items-center gap-2 rounded-full border border-[#F5EFE6]/15 bg-[#F5EFE6]/[0.03] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#F5EFE6]/75 font-street hover:border-[#F5EFE6]/30 hover:text-[#F5EFE6] transition-colors"
          >
            <ShieldCheck size={12} className="opacity-70" />
            {COPY.halalBadge}
          </a>
        </div>
      </div>

      {/* Galerie produits — sous le logo */}
      <div className="mt-10 grid grid-cols-6 gap-3 max-w-4xl mx-auto">
        {/* Pizza — grande image cliquable */}
        <Link
          to="/menu#pizza"
          data-testid="hero-tile-pizza"
          className="col-span-6 sm:col-span-4 relative overflow-hidden rounded-2xl border border-[#262626] bg-[#121212] aspect-[4/3] sm:aspect-auto sm:row-span-2 group"
        >
          <img
            src={ASSETS.pizza}
            alt="Pizzas signatures"
            loading="eager" decoding="async" fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
            <div>
              <div className="font-street text-2xl sm:text-3xl tracking-wider leading-none text-[#F5EFE6] drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                Pizza
              </div>
              <div className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-[#F5EFE6]/20 bg-black/35 backdrop-blur px-2.5 py-0.5 text-[10px] font-street uppercase tracking-[0.22em] text-[#C9A875]">
                Signatures · 10 €
              </div>
            </div>
          </div>
          <NonContractual />
        </Link>

        {/* Naan Burger */}
        <Link
          to="/menu#burgers"
          data-testid="hero-tile-burger"
          className="col-span-3 sm:col-span-2 relative overflow-hidden rounded-2xl border border-[#262626] bg-[#121212] aspect-square group"
        >
          <img
            src={ASSETS.burgerParatha}
            alt="Naan Burger"
            loading="lazy" decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
          <div className="absolute bottom-2 left-2.5 right-2.5">
            <div className="font-street text-sm sm:text-base tracking-wider leading-none text-[#FF9500]">
              Burger
            </div>
          </div>
          <NonContractual />
        </Link>

        {/* Naan */}
        <Link
          to="/menu#naan"
          data-testid="hero-tile-naan"
          className="col-span-3 sm:col-span-2 relative overflow-hidden rounded-2xl border border-[#262626] bg-[#121212] aspect-square group"
        >
          <img
            src={ASSETS.naan}
            alt="Naan"
            loading="lazy" decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
          <div className="absolute bottom-2 left-2.5 right-2.5">
            <div className="font-street text-sm sm:text-base tracking-wider leading-none text-[#FF9500]">
              Naan
            </div>
          </div>
          <NonContractual />
        </Link>

        {/* Merguez — bande large */}
        <Link
          to="/menu#merguez"
          data-testid="hero-tile-merguez"
          className="col-span-6 relative overflow-hidden rounded-2xl border border-[#262626] bg-[#121212] h-28 sm:h-24 group"
        >
          <img
            src={ASSETS.merguez}
            alt="Merguez Classic"
            loading="lazy" decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-y-0 left-4 sm:left-6 flex flex-col justify-center">
            <div className="font-street text-base sm:text-lg tracking-wider leading-none">
              Merguez
            </div>
            <div className="font-street text-base sm:text-lg tracking-wider leading-none text-[#FF9500]">
              Classic
            </div>
          </div>
          <NonContractual />
        </Link>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Raccourcis catégories                                                      */
/* -------------------------------------------------------------------------- */

const shortcuts = [
  { to: "/menu#pizza", label: "Pizza", icon: PizzaIcon },
  { to: "/menu#burgers", label: "Burgers", icon: Beef },
  { to: "/menu#naan", label: "Naan", icon: Sandwich },
  { to: "/menu#merguez", label: "Merguez", icon: Flame },
  { to: "/menu#starters", label: "Starters", icon: UtensilsCrossed },
  { to: "/menu#accompagnements", label: "Accompagnements", icon: Leaf },
  { to: "/menu#boissons", label: "Boissons", icon: Coffee },
  { to: "/menu#desserts", label: "Desserts", icon: IceCream },
  { to: "/offres", label: "Offres", icon: Tag },
];

const Shortcuts = () => (
  <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[#C9A875] font-street">
          La carte en un clin d&apos;œil
        </div>
        <h2 className="font-street mt-2 text-2xl sm:text-3xl tracking-wide">
          Choisissez votre envie
        </h2>
      </div>
      <Link
        to="/menu"
        className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#F5EFE6]/70 font-street hover:text-[#F5EFE6]"
      >
        Carte complète <ArrowRight size={14} />
      </Link>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3">
      {shortcuts.map((s) => (
        <Link
          key={s.to}
          to={s.to}
          data-testid={`shortcut-${s.label.toLowerCase()}`}
          className="group flex items-center gap-2.5 rounded-xl border border-[#262626] bg-[#121212] p-3 sm:p-4 hover:border-[#F5EFE6]/25 hover:-translate-y-0.5 transition-all"
        >
          <span className="inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-md bg-[#C9A875]/8 border border-[#C9A875]/25 text-[#C9A875] group-hover:bg-[#C9A875]/15 group-hover:text-[#E8C895] transition-colors shrink-0">
            <s.icon size={16} strokeWidth={2.4} />
          </span>
          <span className="font-street text-[13px] sm:text-base tracking-wider leading-tight">
            {s.label}
          </span>
          <ArrowRight
            size={13}
            className="ml-auto text-white/30 group-hover:text-[#F5EFE6] transition-colors shrink-0"
          />
        </Link>
      ))}
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Offres preview                                                             */
/* -------------------------------------------------------------------------- */

const formatOfferAvailability = (o) => {
  if (!o.active_days || o.active_days.length === 0) return null;
  const sorted = [...o.active_days].sort((a, b) => a - b);
  const isRange = sorted.every((v, i, arr) => i === 0 || v === arr[i - 1] + 1);
  const full = ["", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
  const days =
    isRange && sorted.length >= 2
      ? `Du ${full[sorted[0]]} au ${full[sorted[sorted.length - 1]]}`
      : sorted.map((d) => full[d]).join(" · ");
  const hours =
    o.start_time && o.end_time
      ? `${o.start_time.replace(":", "h")} – ${o.end_time.replace(":", "h")}`
      : null;
  return hours ? `${days} · ${hours}` : days;
};

const OffersPreview = () => (
  <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[#C9A875] font-street">
          {OFFERS.title}
        </div>
        <h2 className="font-street mt-2 text-2xl sm:text-3xl tracking-wide">
          Bons plans LE 11
        </h2>
      </div>
      <Link
        to="/offres"
        className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#F5EFE6]/70 font-street hover:text-[#F5EFE6]"
      >
        Toutes les offres <ArrowRight size={14} />
      </Link>
    </div>

    <div className="grid gap-4 sm:grid-cols-2">
      {OFFERS.items
        .filter((o) => o.active && o.show_on_offers_page !== false)
        .map((o) => {
          const avail = formatOfferAvailability(o);
          return (
            <Link
              key={o.id}
              to={`/offres#${o.id}`}
              data-testid={`offer-${o.id}`}
              className="group relative overflow-hidden rounded-2xl border border-[#262626] bg-[#121212] p-5 sm:p-6 transition-all hover:border-[#FF9500]/50 hover:-translate-y-0.5"
            >
              {/* Watermark "11" discret, non coupé */}
              <div
                aria-hidden
                className="pointer-events-none absolute right-4 top-3 font-display text-[68px] leading-none text-[#FF9500]/[0.08] select-none"
              >
                11
              </div>
              <div className="relative">
                <div className="font-street text-xl sm:text-2xl tracking-wide">
                  {o.title}
                </div>
                {o.subtitle && (
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#FFC447] font-street mt-1">
                    {o.subtitle}
                  </div>
                )}
                <div className="mt-2 font-display text-3xl text-[#FF9500]">
                  {o.price}
                </div>
                <ul className="mt-3 space-y-1">
                  {(o.composition || []).map((c) => (
                    <li key={c} className="text-xs text-[#D4D4D8] flex items-start gap-2">
                      <span className="inline-block h-1 w-1 rounded-full bg-[#FF9500] mt-1.5 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
                {avail && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#FFC447] font-street">
                    <Clock3 size={12} />
                    {avail}
                  </div>
                )}
                {o.drink_hint && (
                  <div className="mt-2 text-[11px] italic text-white/55 leading-snug">
                    {o.drink_hint}
                  </div>
                )}
                {o.conditions && (
                  <div className="mt-2 text-[10px] italic text-white/40 leading-snug">
                    {o.conditions}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Infos restaurant                                                           */
/* -------------------------------------------------------------------------- */

const InfoBlock = () => (
  <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
    <div className="grid gap-4 sm:grid-cols-3">
      {/* Téléphone */}
      <a
        href={RESTAURANT.phoneTel}
        data-testid="info-phone"
        className="group flex items-start gap-3 rounded-xl border border-[#262626] bg-[#121212] p-5 hover:border-[#FF9500]/50 transition-colors"
      >
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#FF9500] text-black shrink-0">
          <Phone size={16} strokeWidth={2.6} />
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-street">
            Téléphone
          </div>
          <div className="font-display text-xl text-white mt-0.5 group-hover:text-[#FF9500] transition-colors">
            {RESTAURANT.phoneDisplay}
          </div>
        </div>
      </a>

      {/* Adresse */}
      <div className="flex items-start gap-3 rounded-xl border border-[#262626] bg-[#121212] p-5">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A875]/10 border border-[#C9A875]/25 text-[#C9A875] shrink-0">
          <MapPin size={16} />
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-street">
            Adresse
          </div>
          <div className="text-sm text-white mt-1 leading-snug">
            {RESTAURANT.address.street}
            <br />
            {RESTAURANT.address.zipCity}
          </div>
        </div>
      </div>

      {/* Horaires */}
      <div className="flex items-start gap-3 rounded-xl border border-[#262626] bg-[#121212] p-5">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A875]/10 border border-[#C9A875]/25 text-[#C9A875] shrink-0">
          <Clock3 size={16} />
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-street">
            Horaires
          </div>
          <ul className="mt-1 space-y-0.5">
            {RESTAURANT.hours.map((h) => (
              <li key={h.days} className="text-xs text-[#D4D4D8] leading-snug">
                <span className="text-white/60">{h.days}</span> · {h.range}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Plateformes (bientôt disponible)                                           */
/* -------------------------------------------------------------------------- */

const PlatformsBlock = () => (
  <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
    <div className="rounded-2xl border border-[#262626] bg-[#121212] p-6 sm:p-7">
      <div className="grid gap-5 lg:grid-cols-12 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.28em] text-[#C9A875] font-street">
            Plateformes
          </div>
          <h3 className="font-street mt-2 text-xl sm:text-2xl tracking-wide">
            Livraison bientôt disponible
          </h3>
          <p className="mt-2 text-sm text-[#A1A1AA] leading-relaxed max-w-md">
            Retrouvez prochainement {RESTAURANT.name} sur vos plateformes de
            livraison habituelles.
          </p>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-3">
          {PLATFORMS.map((p) => {
            const active = p.status === "active" && p.url;
            return (
              <div
                key={p.id}
                data-testid={`home-platform-${p.id}`}
                aria-disabled={!active}
                className={`relative flex items-center justify-between gap-2 rounded-lg border border-[#262626] bg-[#0A0A0A] px-4 py-3 ${
                  active ? "hover:border-white/30" : "opacity-70 cursor-not-allowed"
                }`}
              >
                <span
                  className="font-street text-base tracking-wider"
                  style={{ color: p.color }}
                >
                  {p.name}
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-street">
                  Bientôt disponible
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Halal compact                                                              */
/* -------------------------------------------------------------------------- */

const HalalCompact = () => (
  <section id="halal" className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
    <div className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A875]/10 border border-[#C9A875]/25 text-[#C9A875] shrink-0">
          <ShieldCheck size={18} />
        </span>
        <div className="flex-1">
          <div className="font-street text-base sm:text-lg tracking-wide uppercase">
            {HALAL.title}
          </div>
          <p className="mt-1 text-xs sm:text-sm text-[#A1A1AA] leading-relaxed">
            {HALAL.text}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          {HALAL.organisations.map((o) => (
            <span
              key={o}
              className="font-street text-[10px] uppercase tracking-[0.22em] rounded-md border border-[#262626] bg-[#0A0A0A] px-2.5 py-1 text-white/80"
            >
              {o}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Home — page                                                                */
/* -------------------------------------------------------------------------- */

const RestaurantBlock = () => {
  const r = useRestaurant();
  const mapsUrl =
    r.maps_url ||
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${r.address_street} ${r.address_zip} ${r.address_city}`
    )}`;

  if (!r.photo_url) return null;

  return (
    <section
      data-testid="home-restaurant-block"
      className="relative mx-auto max-w-6xl px-4 sm:px-6 py-14 sm:py-20"
    >
      {/* halo bleu nuit derrière le bloc */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 mx-auto h-[420px] max-w-4xl"
        style={{
          background:
            "radial-gradient(55% 80% at 50% 50%, rgba(30,58,95,0.22) 0%, transparent 70%)",
        }}
      />

      <div className="relative grid gap-8 lg:gap-10 lg:grid-cols-12 items-center">
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="home-restaurant-photo"
          className="lg:col-span-7 group relative block overflow-hidden rounded-2xl border border-[#1F1F1F] bg-[#0F0F0F] h-72 sm:h-[420px] lg:h-[480px] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]"
        >
          <img
            src={r.photo_url}
            alt={`${r.name} — devanture du restaurant à ${r.address_city}`}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.025]"
          />
          {/* voile bas pour lisibilité, voile haut très léger pour profondeur */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/20" />
          {/* badge "ville" en haut à gauche */}
          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/40 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] font-street text-[#F5EFE6]">
            <MapPin size={11} strokeWidth={2.4} />
            {r.address_city}
          </div>
          {/* CTA itinéraire en bas */}
          <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-3">
            <div className="font-serif-brand text-3xl sm:text-4xl text-[#F5EFE6] leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              {r.name}
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] font-street text-[#FFC447] opacity-0 group-hover:opacity-100 transition-opacity">
              Itinéraire <ExternalLink size={11} />
            </span>
          </div>
        </a>

        <div className="lg:col-span-5 flex flex-col justify-center">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#C9A875] font-street">
            Le restaurant
          </div>
          <h2 className="font-serif-brand text-4xl sm:text-5xl text-[#F5EFE6] leading-[1.05] mt-3">
            Passez nous voir au comptoir.
          </h2>
          <p className="mt-5 text-sm sm:text-base text-[#A1A1AA] leading-relaxed">
            Retrouvez {r.name} au {r.address_street}, {r.address_zip}{" "}
            {r.address_city}. Repas à emporter ou sur place, fait maison.
          </p>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="home-restaurant-maps-cta"
              className="font-street inline-flex items-center gap-2 rounded-md border border-[#F5EFE6]/20 bg-[#F5EFE6]/[0.03] px-4 py-2.5 text-[12px] tracking-wider text-[#F5EFE6] hover:border-[#F5EFE6]/35 hover:bg-[#F5EFE6]/[0.06] transition-colors"
            >
              <MapPin size={13} strokeWidth={2.4} className="text-[#C9A875]" /> Voir l&apos;itinéraire
            </a>
            <a
              href={r.phone_tel || "tel:+33139941919"}
              data-testid="home-restaurant-phone-cta"
              className="font-street inline-flex items-center gap-2 rounded-md border border-[#F5EFE6]/15 bg-transparent px-4 py-2.5 text-[12px] tracking-wider text-[#F5EFE6]/85 hover:text-[#F5EFE6] hover:border-[#F5EFE6]/30 transition-colors"
            >
              <Phone size={13} strokeWidth={2.4} className="text-[#C9A875]" /> {r.phone_display}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <div data-testid={HOME.root} className="bg-[#0A0A0A] text-white">
    <Seo
      title="LE 11 - Pizza personnalisée, burgers et cheese naan à Villiers-le-Bel"
      description="LE 11 à Villiers-le-Bel : pizza personnalisée, burgers paratha, cheese naan, sandwich merguez, starters, desserts et commande à emporter par téléphone."
    />
    <Hero />
    <RestaurantBlock />
    <HowToOrderBlock variant="home" />
    <Shortcuts />
    <OffersPreview />
    <InfoBlock />
    <PlatformsBlock />
    <HalalCompact />
  </div>
);

export default Home;
